import {
  Menu,
  MenuButton,
  MenuItem,
  MenuItems,
  Transition,
} from "@headlessui/react";
import { ArrowDownTrayIcon } from "@heroicons/react/24/outline";
import React, { type FC, Fragment } from "react";

export interface ExportDropdownProps {
  /** Whether report (HTML) download is available */
  hasReport: boolean;
  /** Whether chat JSON export is available */
  hasChatExport: boolean;
  /** Disabled when streaming */
  isStreaming: boolean;
  onDownloadReport: () => void;
  onExportChat: () => void;
}

export const ExportDropdown: FC<ExportDropdownProps> = ({
  hasReport,
  hasChatExport,
  isStreaming,
  onDownloadReport,
  onExportChat,
}) => {
  const isDisabled = isStreaming || (!hasReport && !hasChatExport);

  return (
    <Menu as="div" className="relative shrink-0">
      <MenuButton
        data-testid="reboot-live-export-button"
        disabled={isDisabled}
        className={`box-border content-stretch flex gap-2 h-10 items-center justify-center overflow-clip px-2.5 py-1.75 relative rounded-md shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)] shrink-0 transition-opacity ${
          isDisabled
            ? "bg-gray-500 opacity-50 cursor-not-allowed"
            : "bg-orange-500 hover:opacity-90 cursor-pointer"
        }`}
      >
        <ArrowDownTrayIcon className="w-5 h-5 text-white shrink-0" />
        <p className="font-semibold leading-5 not-italic relative shrink-0 text-base text-nowrap whitespace-pre text-white">
          Export
        </p>
      </MenuButton>

      <Transition
        as={Fragment}
        enter="transition ease-out duration-100"
        enterFrom="transform opacity-0 scale-95"
        enterTo="transform opacity-100 scale-100"
        leave="transition ease-in duration-75"
        leaveFrom="transform opacity-100 scale-100"
        leaveTo="transform opacity-0 scale-95"
      >
        <MenuItems className="absolute right-0 mt-2 w-56 origin-top-right rounded-[6px] bg-[#1d1d1f] border border-[rgba(255,255,255,0.1)] shadow-[0px_10px_15px_-3px_rgba(0,0,0,0.1),0px_4px_6px_-2px_rgba(0,0,0,0.05)] focus:outline-none z-[9999999] overflow-hidden">
          {/* REPORT section */}
          <div className="py-1">
            <div className="px-4 py-2">
              <p className="text-[#7e7e81] text-[11px] font-medium uppercase tracking-wider">
                Report
              </p>
            </div>
            <MenuItem disabled={!hasReport || isStreaming}>
              {({ focus }) => (
                <button
                  type="button"
                  data-testid="export-dropdown-html-document"
                  onClick={onDownloadReport}
                  disabled={!hasReport || isStreaming}
                  className={`flex w-full items-center px-4 py-2 text-[14px] transition-colors ${
                    !hasReport || isStreaming
                      ? "text-[#555] cursor-not-allowed"
                      : focus
                        ? "bg-[#252527] text-white"
                        : "text-white"
                  }`}
                >
                  HTML Document
                </button>
              )}
            </MenuItem>
          </div>

          <div className="h-px bg-[rgba(255,255,255,0.06)]" />

          {/* CHAT SESSION section */}
          <div className="py-1">
            <div className="px-4 py-2">
              <p className="text-[#7e7e81] text-[11px] font-medium uppercase tracking-wider">
                Chat Session
              </p>
            </div>
            <MenuItem disabled={!hasChatExport || isStreaming}>
              {({ focus }) => (
                <button
                  type="button"
                  data-testid="export-dropdown-json"
                  onClick={onExportChat}
                  disabled={!hasChatExport || isStreaming}
                  className={`flex w-full items-center gap-2 px-4 py-2 text-[14px] transition-colors ${
                    !hasChatExport || isStreaming
                      ? "text-[#555] cursor-not-allowed"
                      : focus
                        ? "bg-[#252527] text-white"
                        : "text-white"
                  }`}
                >
                  <ArrowDownTrayIcon
                    className={`w-4 h-4 shrink-0 ${
                      !hasChatExport || isStreaming
                        ? "text-[#555]"
                        : "text-orange-500"
                    }`}
                  />
                  Export as JSON
                </button>
              )}
            </MenuItem>
          </div>
        </MenuItems>
      </Transition>
    </Menu>
  );
};
